// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd/lib/grid';
import Checkbox, { CheckboxChangeEvent } from 'antd/lib/checkbox';
import Text from 'antd/lib/typography/Text';
import InputNumber from 'antd/lib/input-number';
import Select from 'antd/lib/select';
import Slider from 'antd/lib/slider';
import Button from 'antd/lib/button';

import {
    switchGrid,
    changeGridColor,
    changeGridOpacity,
    changeBrightnessLevel,
    changeContrastLevel,
    changeSaturationLevel,
    changeGridSize,
    resetImageFilters,
} from 'actions/settings-actions';
import { clamp } from 'utils/math';
import { GridColor, CombinedState, PlayerSettingsState } from 'reducers';
import {
    updateStoredColorSettings,
    resetStoredColorSettings,
    getStoredColorSettings,
} from 'utils/color-settings-storage';
import GammaFilter from './gamma-filter';

const minGridSize = 5;
const maxGridSize = 1000;
const brightnessMin = 50;
const brightnessMax = 200;
const contrastMin = 50;
const contrastMax = 200;
const saturationMin = 0;
const saturationMax = 300;
const defaultColorSetting = 100;

export default function ImageSetupsContent(): JSX.Element {
    const dispatch = useDispatch();
    const jobId = useSelector((state: CombinedState) => state.annotation.job.instance?.id);
    const frameNumber = useSelector((state: CombinedState) => state.annotation.player.frame.number);
    const {
        brightnessLevel,
        contrastLevel,
        saturationLevel,
        gridOpacity,
        gridColor,
        gridSize,
        grid: gridEnabled,
    } = useSelector((state: CombinedState): PlayerSettingsState => state.settings.player);

    useEffect(() => {
        if (!jobId) {
            return;
        }

        const stored = getStoredColorSettings({ jobId, frame: frameNumber });
        const storedBrightness = stored.brightnessLevel ?? defaultColorSetting;
        const storedContrast = stored.contrastLevel ?? defaultColorSetting;
        const storedSaturation = stored.saturationLevel ?? defaultColorSetting;

        if (storedBrightness !== brightnessLevel) {
            dispatch(changeBrightnessLevel(storedBrightness));
        }
        if (storedContrast !== contrastLevel) {
            dispatch(changeContrastLevel(storedContrast));
        }
        if (storedSaturation !== saturationLevel) {
            dispatch(changeSaturationLevel(storedSaturation));
        }
    }, [jobId, frameNumber]);
    return (
        <div className='cvat-canvas-image-setups-content'>
            <Text>Image grid</Text>
            <hr />
            <Row justify='space-between' align='middle' gutter={8}>
                <Col span={1} />
                <Col span={6}>
                    <Text className='cvat-text-color'> Size </Text>
                </Col>
                <Col span={8}>
                    <Text className='cvat-text-color'> Color </Text>
                </Col>
                <Col span={8}>
                    <Text className='cvat-text-color'> Opacity </Text>
                </Col>
            </Row>
            <Row justify='space-between' align='middle' gutter={8}>
                <Col span={1}>
                    <Checkbox
                        className='cvat-text-color cvat-image-setups-grid'
                        checked={gridEnabled}
                        onChange={(event: CheckboxChangeEvent): void => {
                            dispatch(switchGrid(event.target.checked));
                        }}
                    />
                </Col>
                <Col span={6} className='cvat-image-setups-grid-size'>
                    <InputNumber
                        className='cvat-image-setups-grid-size-input'
                        min={minGridSize}
                        max={maxGridSize}
                        value={gridSize}
                        disabled={!gridEnabled}
                        onChange={(value: number | undefined | null | string): void => {
                            if (typeof value !== 'undefined' && value !== null) {
                                const converted = Math.floor(clamp(+value, minGridSize, maxGridSize));
                                dispatch(changeGridSize(converted));
                            }
                        }}
                    />
                </Col>
                <Col span={8} className='cvat-image-setups-grid-color'>
                    <Select
                        className='cvat-image-setups-grid-color-input'
                        value={gridColor}
                        disabled={!gridEnabled}
                        onChange={(color: GridColor): void => {
                            dispatch(changeGridColor(color));
                        }}
                    >
                        <Select.Option key='white' value={GridColor.White}>
                            White
                        </Select.Option>
                        <Select.Option key='black' value={GridColor.Black}>
                            Black
                        </Select.Option>
                        <Select.Option key='red' value={GridColor.Red}>
                            Red
                        </Select.Option>
                        <Select.Option key='green' value={GridColor.Green}>
                            Green
                        </Select.Option>
                        <Select.Option key='blue' value={GridColor.Blue}>
                            Blue
                        </Select.Option>
                    </Select>
                </Col>
                <Col span={8} className='cvat-image-setups-grid-opacity'>
                    <Slider
                        className='cvat-image-setups-grid-opacity-input'
                        min={0}
                        max={100}
                        value={gridOpacity}
                        disabled={!gridEnabled}
                        onChange={(value: number | [number, number]): void => {
                            dispatch(changeGridOpacity(value as number));
                        }}
                    />
                </Col>
            </Row>
            <Text>Color settings</Text>
            <hr />
            <Row justify='space-around'>
                <Col span={24}>
                    <Row className='cvat-image-setups-brightness'>
                        <Col span={6}>
                            <Text className='cvat-text-color'> Brightness </Text>
                        </Col>
                        <Col span={12}>
                            <Slider
                                min={brightnessMin}
                                max={brightnessMax}
                                value={brightnessLevel}
                                onChange={(value: number | [number, number]): void => {
                                    const level = value as number;
                                    updateStoredColorSettings({ jobId, frame: frameNumber }, {
                                        brightnessLevel: level === defaultColorSetting ? undefined : level,
                                    });
                                    dispatch(changeBrightnessLevel(level));
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-contrast'>
                        <Col span={6}>
                            <Text className='cvat-text-color'> Contrast </Text>
                        </Col>
                        <Col span={12}>
                            <Slider
                                min={contrastMin}
                                max={contrastMax}
                                value={contrastLevel}
                                onChange={(value: number | [number, number]): void => {
                                    const level = value as number;
                                    updateStoredColorSettings({ jobId, frame: frameNumber }, {
                                        contrastLevel: level === defaultColorSetting ? undefined : level,
                                    });
                                    dispatch(changeContrastLevel(level));
                                }}
                            />
                        </Col>
                    </Row>
                    <Row className='cvat-image-setups-saturation'>
                        <Col span={6}>
                            <Text className='cvat-text-color'> Saturation </Text>
                        </Col>
                        <Col span={12}>
                            <Slider
                                min={saturationMin}
                                max={saturationMax}
                                value={saturationLevel}
                                onChange={(value: number | [number, number]): void => {
                                    const level = value as number;
                                    updateStoredColorSettings({ jobId, frame: frameNumber }, {
                                        saturationLevel: level === defaultColorSetting ? undefined : level,
                                    });
                                    dispatch(changeSaturationLevel(level));
                                }}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
            <GammaFilter />
            <Row className='cvat-image-setups-reset-color-settings' justify='space-around'>
                <Col>
                    <Button
                        className='cvat-image-setups-reset-color-settings-button'
                        onClick={() => {
                            resetStoredColorSettings({ jobId, frame: frameNumber });
                            dispatch(changeBrightnessLevel(defaultColorSetting));
                            dispatch(changeContrastLevel(defaultColorSetting));
                            dispatch(changeSaturationLevel(defaultColorSetting));
                            dispatch(resetImageFilters());
                        }}
                    >
                        Reset color settings
                    </Button>
                </Col>
            </Row>
        </div>
    );
}
